import { Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./auth";

// Marketing pages are the public front door — keep them eager so the landing
// renders instantly and crawlers see content without waiting on a chunk.
import { Home } from "./marketing/Home";
import { Platform } from "./marketing/Platform";
import { Pricing } from "./marketing/Pricing";
import { SecurityPage } from "./marketing/SecurityPage";
import { About } from "./marketing/About";
import { Contact } from "./marketing/Contact";
import { Careers } from "./marketing/Careers";
import { Blog } from "./marketing/Blog";
import { BlogPost } from "./marketing/BlogPost";
import { Privacy, Terms } from "./marketing/Legal";
import { NotFound } from "./marketing/NotFound";

// The authenticated portal + candidate flows (incl. Stripe) are code-split so
// marketing visitors never download them.
const Login = lazy(() => import("./pages/Login").then((m) => ({ default: m.Login })));
const Signup = lazy(() => import("./pages/Signup").then((m) => ({ default: m.Signup })));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/ResetPassword").then((m) => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail").then((m) => ({ default: m.VerifyEmail })));
const CandidateInterview = lazy(() => import("./pages/CandidateInterview").then((m) => ({ default: m.CandidateInterview })));
const SharedReport = lazy(() => import("./pages/SharedReport").then((m) => ({ default: m.SharedReport })));
const Dashboard = lazy(() => import("./pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const Interviews = lazy(() => import("./pages/Interviews").then((m) => ({ default: m.Interviews })));
const NewInterview = lazy(() => import("./pages/NewInterview").then((m) => ({ default: m.NewInterview })));
const EditInterview = lazy(() => import("./pages/EditInterview").then((m) => ({ default: m.EditInterview })));
const Candidates = lazy(() => import("./pages/Candidates").then((m) => ({ default: m.Candidates })));
const CandidateReport = lazy(() => import("./pages/CandidateReport").then((m) => ({ default: m.CandidateReport })));
const Billing = lazy(() => import("./pages/Billing").then((m) => ({ default: m.Billing })));
const Team = lazy(() => import("./pages/Team").then((m) => ({ default: m.Team })));
const Settings = lazy(() => import("./pages/Settings").then((m) => ({ default: m.Settings })));
const Security = lazy(() => import("./pages/Security").then((m) => ({ default: m.Security })));
const AuditLog = lazy(() => import("./pages/AuditLog").then((m) => ({ default: m.AuditLog })));
const Review = lazy(() => import("./pages/Review").then((m) => ({ default: m.Review })));
const Admin = lazy(() => import("./pages/Admin").then((m) => ({ default: m.Admin })));
const AdminApp = lazy(() => import("./pages/platform").then((m) => ({ default: m.AdminApp })));

export function App() {
  return (
    <Suspense fallback={<Splash />}>
    <Routes>
      {/* Public marketing site */}
      <Route path="/" element={<Home />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* Public candidate interview experience */}
      <Route path="/interview/:token" element={<CandidateInterview />} />
      <Route path="/r/:token" element={<SharedReport />} />
      <Route path="/verify/:token" element={<VerifyEmail />} />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset/:token" element={<ResetPassword />} />

      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/team" element={<Protected><Team /></Protected>} />
      <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/app/security" element={<Protected><Security /></Protected>} />
      <Route path="/app/audit" element={<Protected><AuditLog /></Protected>} />
      <Route path="/app/review" element={<Protected><Review /></Protected>} />
      <Route path="/app/interviews" element={<Protected><Interviews /></Protected>} />
      <Route path="/app/interviews/new" element={<Protected><NewInterview /></Protected>} />
      <Route path="/app/interviews/:id/edit" element={<Protected><EditInterview /></Protected>} />
      <Route path="/app/candidates" element={<Protected><Candidates /></Protected>} />
      <Route path="/app/candidates/:id" element={<Protected><CandidateReport /></Protected>} />
      <Route path="/app/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/app/admin" element={<Protected admin><Admin /></Protected>} />

      {/* Separate Platform Admin area (own shell + guard) — not nested in /app. */}
      <Route path="/admin/*" element={<AdminApp />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function Splash() {
  return <div className="flex h-screen items-center justify-center text-white/60">Loading…</div>;
}

function Protected({ children, admin }: { children: ReactNode; admin?: boolean }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (admin && !user.isPlatformAdmin) return <Navigate to="/app" replace />;
  return <Shell>{children}</Shell>;
}

function Shell({ children }: { children: ReactNode }) {
  const { user, company, logout } = useAuth();
  const nav = useNavigate();
  const link = (to: string, label: string) => (
    <Link to={to} className="block rounded-lg px-3 py-2 text-sm text-white/70 hover:bg-white/10 hover:text-white">
      {label}
    </Link>
  );
  return (
    <div className="flex min-h-screen text-white">
      <aside className="w-60 shrink-0 border-r border-white/10 bg-black/30 p-4">
        <div className="mb-6 px-2 text-lg font-bold">Wonderful<span className="text-brand">Interview</span></div>
        <nav className="space-y-1">
          {link("/app", "Dashboard")}
          {link("/app/interviews", "Interviews")}
          {link("/app/candidates", "Candidates")}
          {link("/app/billing", "Billing")}
          {link("/app/review", "Review queue")}
          {(user?.role === "owner" || user?.role === "admin") && link("/app/team", "Team")}
          {(user?.role === "owner" || user?.role === "admin") && link("/app/settings", "Settings")}
          {link("/app/security", "Security")}
          {(user?.role === "owner" || user?.role === "admin") && link("/app/audit", "Audit log")}
          {user?.isPlatformAdmin && link("/admin", "Platform Admin")}
        </nav>
        <div className="absolute bottom-4 w-52">
          <div className="px-3 text-xs text-white/40">{company?.name}</div>
          <div className="px-3 text-xs text-white/40">{user?.email}</div>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-2 w-full rounded-lg px-3 py-2 text-left text-sm text-white/70 hover:bg-white/10"
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-8">{children}</main>
    </div>
  );
}
